// Prototype feature flag — gates pages that aren't yet wired to real backend data.
//
// Default: HIDDEN in production (cleanest possible UX, no fake demo experiences).
// Reveal: set REACT_APP_SHOW_PROTOTYPES=1 in /app/frontend/.env, OR append
// `?prototypes=1` to the URL (sticks via localStorage for the session).
//
// Pages currently flagged as "prototype" (sample/demo data, not yet wired):
//   - AI Risk & Forecasting    (/ai-insights)
//   - Offer Intelligence       (/offer-intelligence)
//   - Marketplace              (/marketplace)
//   - Seller Launch Hub        (/seller-launch-hub)
//   - Buyer Portal             (/buyer-portal)
//   - Seller Portal            (/seller-portal)
//   - Documents Page           (/documents)            — sample seed array
//   - Today's Queue            (/command)              — sample seed array
//
// When a flagged page is hit with the flag OFF, we render the existing
// Placeholder ("In design · Q2 2026") instead of the prototype shell.
// This keeps the route registered (no 404 on deep links) while making
// it absolutely clear this isn't real production data.

const FLAG_LS_KEY = "chainflow:show_prototypes";

const readQueryFlag = () => {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("prototypes") === "1") {
      window.localStorage.setItem(FLAG_LS_KEY, "1");
      return true;
    }
    if (params.get("prototypes") === "0") {
      window.localStorage.removeItem(FLAG_LS_KEY);
      return false;
    }
  } catch (_e) { /* noop */ }
  try {
    return window.localStorage.getItem(FLAG_LS_KEY) === "1";
  } catch (_e) { return false; }
};

export const showPrototypes = () => {
  // 1) Build-time env flag (production override)
  if (process.env.REACT_APP_SHOW_PROTOTYPES === "1") return true;
  // 2) Runtime query string + localStorage (dev/demo escape hatch)
  return readQueryFlag();
};

// Catalogue of every page currently on a prototype/sample data path.
// Keys MUST match the route paths so the feature flag and the sidebar
// stay in sync from one place.
export const PROTOTYPE_ROUTES = {
  "/ai-insights":         { title: "AI Risk & Forecasting", subtitle: "Predictive risk scoring + chain forecasts powered by ChainFlow AI. We're polishing the data pipeline before opening this up." },
  "/offer-intelligence":  { title: "Offer Intelligence",    subtitle: "Smart offer ranking + automated negotiation insights. Coming once we've finished wiring it to your real deals." },
  "/marketplace":         { title: "Marketplace",           subtitle: "Buyer-side property discovery and AI matching. Live to estate agents in Q2 2026." },
  "/seller-launch-hub":   { title: "Seller Launch Hub",     subtitle: "Pre-listing checklist, vendor onboarding and automated launch packs. Currently in private design review." },
  "/buyer-portal":        { title: "Buyer Portal",          subtitle: "Self-serve buyer experience with progress tracking and document e-signing. Opening to early-access agencies in Q2 2026." },
  "/seller-portal":       { title: "Seller Portal",         subtitle: "Vendor-facing dashboard with stage tracking, viewings calendar and offer history. In private beta." },
  "/documents":           { title: "Documents",             subtitle: "Central document vault — once Supabase Storage is wired in, this surfaces every contract, ID and survey across your deals." },
  "/command":             { title: "Today's Queue",         subtitle: "Your morning operations queue — tasks, offers and chain risks ranked by urgency. Wiring up to live tasks/notifications shortly." },
};

export const isPrototypePath = (pathname = "") => {
  // Match exact path or any prefix path (e.g. /seller-launch-hub/:propertyId).
  const p = pathname.split("?")[0].split("#")[0];
  for (const route of Object.keys(PROTOTYPE_ROUTES)) {
    if (p === route || p.startsWith(route + "/")) return true;
  }
  return false;
};
